import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CustomerLayout from '../components/CustomerLayout';
import { customerAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import '../App.css';

export default function Dashboard() {
    const { user } = useAuth();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const response = await customerAPI.getDashboard();
            setDashboardData(response.data);
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD'
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-AU');
    };

    const getStatusLabel = (status) => {
        switch (status?.toLowerCase()) {
            case 'paid': return 'Paid';
            case 'unpaid': return 'Unpaid';
            case 'overdue': return 'Overdue';
            case 'pending': return 'Awaiting Payment';
            case 'draft': return 'Draft';
            default: return status || 'Unknown';
        }
    };

    return (
        <CustomerLayout>
            <div style={{ maxWidth: '1200px' }}>
                <h1 style={{ marginBottom: '10px' }}>Welcome, {user?.customerName || user?.name || 'Customer'}!</h1>
                <p style={{ color: '#666', marginBottom: '30px' }}>
                    {user?.email || 'Your Account'}
                </p>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px' }}>
                        <div className="spinner"></div>
                    </div>
                ) : (
                    <>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '20px',
                            marginBottom: '30px'
                        }}>
                            <div className="card">
                                <h3 style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>Outstanding Balance</h3>
                                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#007bff' }}>
                                    {formatCurrency(dashboardData?.stats?.outstandingBalance || 0)}
                                </p>
                            </div>
                            <div className="card">
                                <h3 style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>Active Contracts</h3>
                                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#28a745' }}>
                                    {dashboardData?.stats?.activeContracts || 0}
                                </p>
                            </div>
                            <div className="card">
                                <h3 style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>Open Tickets</h3>
                                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#ffc107' }}>
                                    {dashboardData?.stats?.openTickets || 0}
                                </p>
                            </div>
                            <div className="card">
                                <h3 style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>Total Assets</h3>
                                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#17a2b8' }}>
                                    {dashboardData?.stats?.totalAssets || 0}
                                </p>
                            </div>
                        </div>

                        {/* Recent Invoices */}
                        {dashboardData?.recentInvoices && dashboardData.recentInvoices.length > 0 && (
                            <div className="card" style={{ marginBottom: '20px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <h2>Recent Invoices</h2>
                                    <Link to="/invoices" style={{ color: '#007bff', textDecoration: 'none' }}>
                                        View All →
                                    </Link>
                                </div>
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Invoice #</th>
                                            <th>Date</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dashboardData.recentInvoices.map((invoice) => (
                                            <tr key={invoice.invoice_id}>
                                                <td><strong>{invoice.invoice_number}</strong></td>
                                                <td>{formatDate(invoice.invoice_date)}</td>
                                                <td><strong>{formatCurrency(invoice.total)}</strong></td>
                                                <td>
                                                    <span className={`status-badge ${invoice.status?.toLowerCase()}`}>
                                                        {getStatusLabel(invoice.status)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Recent Tickets */}
                        {dashboardData?.recentTickets && dashboardData.recentTickets.length > 0 && (
                            <div className="card" style={{ marginBottom: '20px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <h2>Recent Tickets</h2>
                                    <Link to="/tickets" style={{ color: '#007bff', textDecoration: 'none' }}>
                                        View All →
                                    </Link>
                                </div>
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Ticket #</th>
                                            <th>Subject</th>
                                            <th>Status</th>
                                            <th>Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dashboardData.recentTickets.map((ticket) => (
                                            <tr key={ticket.ticket_id}>
                                                <td><strong>{ticket.ticket_number}</strong></td>
                                                <td>{ticket.subject}</td>
                                                <td>
                                                    <span className={`status-badge ${ticket.status?.toLowerCase().replace(' ', '-')}`}>
                                                        {ticket.status}
                                                    </span>
                                                </td>
                                                <td>{formatDate(ticket.created_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Quick Actions */}
                        <div className="card">
                            <h2 style={{ marginBottom: '20px' }}>Quick Actions</h2>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                <Link to="/invoices">
                                    <button className="btn btn-primary">View Invoices</button>
                                </Link>
                                <Link to="/tickets">
                                    <button className="btn btn-secondary">Create Ticket</button>
                                </Link>
                                <Link to="/contracts">
                                    <button className="btn btn-secondary">View Contracts</button>
                                </Link>
                                <Link to="/marketplace">
                                    <button className="btn btn-secondary">Browse Services</button>
                                </Link>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </CustomerLayout>
    );
}
